import { useState } from 'react';
import type { InvestigationResponse, KpiGroupBlock, OpsBriefBlock } from '../../types/investigator';
import type { ProfitAgentDrilldown } from '../../types/profitabilityAgent';
import AssistantAnswerCard from './AssistantAnswerCard';
import AgentBadge from './AgentBadge';
import CollapsibleSection from './CollapsibleSection';
import MoreDetail from './MoreDetail';
import EvidencePanel from '../investigator/EvidencePanel';
import TimelinePanel from '../investigator/TimelinePanel';
import FollowUps from '../investigator/FollowUps';
import AnswerFeedbackControl from './AnswerFeedbackControl';
import BriefProposalCard from './BriefProposalCard';
import { useOptionalTab } from '../../contexts/TabContext';
import { filterTimelineEvidence } from '../../utils/timelineDisplay';

interface ZoeTurnCardProps {
  result: InvestigationResponse;
  /** Resolved label for `result.agent_id` (registry name, or a humanised
   *  fallback) — omitted entirely when the turn carries no attribution. */
  agentLabel?: string;
  activeProfitDrilldownId: string | null;
  onProfitDrilldown: (d: ProfitAgentDrilldown) => void;
  onOpenProfitabilityReport: (d: ProfitAgentDrilldown) => void;
  onSelectFollowUp: (question: string) => void;
  /** The answer already finished typing on another surface: show it settled. */
  revealed?: boolean;
  /** Called once this answer has finished typing. */
  onRevealed?: () => void;
}

/**
 * I1-E6 — one turn's full answer (narrative + structural panels), extracted
 * from what used to be Agents.tsx's single global result block so each turn
 * in the thread owns its own evidence-panel focus / streaming-complete gate
 * instead of sharing one set of state across the whole conversation.
 */
export default function ZoeTurnCard({
  result,
  agentLabel,
  activeProfitDrilldownId,
  onProfitDrilldown,
  onOpenProfitabilityReport,
  onSelectFollowUp,
  revealed = false,
  onRevealed,
}: ZoeTurnCardProps) {
  const [evidenceSectionOpen, setEvidenceSectionOpen] = useState(false);
  const [evidenceFocusIndex, setEvidenceFocusIndex] = useState<number | null>(null);
  const [evidenceFocusNonce, setEvidenceFocusNonce] = useState(0);
  const [moreDetailOpen, setMoreDetailOpen] = useState(false);
  const [zoeStreamingComplete, setZoeStreamingComplete] = useState(revealed);
  const tab = useOptionalTab();

  // T115 — under every answer, what does not answer the question waits behind
  // one closed "More detail": summary strip, figures, timeline and evidence.
  const blocks = result.presentation?.blocks ?? [];
  const brief = blocks.find((b): b is OpsBriefBlock => b.type === 'ops_brief');
  const figures = blocks.find((b): b is KpiGroupBlock => b.type === 'kpi_group')?.items ?? [];
  // Without the narrator the summary strip IS the answer's line: not repeated.
  const showSummary = result.presentation?.answer_source === 'narrator';
  // A pure Profit Agent answer draws its own card and has nothing more to show.
  const pureProfit = Boolean(result.profit_delegation) && (result.evidence?.length ?? 0) === 0;

  const handleEvidenceRefFromAnswer = (oneBased: number) => {
    const list = result.evidence;
    if (!list?.length) return;
    const idx = oneBased - 1;
    if (idx < 0 || idx >= list.length) return;
    setMoreDetailOpen(true);
    setEvidenceSectionOpen(true);
    setEvidenceFocusIndex(idx);
    setEvidenceFocusNonce((n) => n + 1);
  };

  // Skip timeline/evidence when there is nothing to show (e.g. pure profit delegation).
  const showEvidence = (result.evidence?.length ?? 0) > 0 && result.intent !== 'capabilities_help';
  const timelineCount = filterTimelineEvidence(result.evidence ?? []).length;
  const timelineAndEvidence = showEvidence ? (
    <div className="space-y-3">
      <CollapsibleSection
        title={
          <>
            Timeline{' '}
            <span className="text-success font-medium">({timelineCount})</span>
          </>
        }
        subtitle="Events related to this investigation"
      >
        <div className="min-h-65">
          <TimelinePanel evidence={result.evidence} showIntroHeader={false} showRouteHeader />
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title={
          <>
            Evidence{' '}
            <span className="text-success font-medium">({result.evidence?.length ?? 0})</span>
          </>
        }
        subtitle="Sources and refs for this answer"
        open={evidenceSectionOpen}
        onOpenChange={setEvidenceSectionOpen}
      >
        <div className="min-h-65">
          <EvidencePanel
            evidence={result.evidence}
            focusedEvidenceIndex={evidenceFocusIndex}
            evidenceFocusNonce={evidenceFocusNonce}
          />
        </div>
      </CollapsibleSection>
    </div>
  ) : null;

  return (
    <div className="space-y-3">
      {agentLabel ? <AgentBadge label={agentLabel} /> : null}

      <AssistantAnswerCard
        data={result}
        onEvidenceRefClick={handleEvidenceRefFromAnswer}
        onStreamingComplete={() => {
          setZoeStreamingComplete(true);
          onRevealed?.();
        }}
        disableStreaming={revealed}
        activeProfitDrilldownId={activeProfitDrilldownId}
        onProfitDrilldown={onProfitDrilldown}
        onOpenProfitabilityReport={onOpenProfitabilityReport}
      />

      {result.brief_proposal && zoeStreamingComplete ? (
        <div className="zoe-snap-in">
          <BriefProposalCard
            proposal={result.brief_proposal}
            onOpenBriefs={tab ? () => tab.setActiveTab('zoe-briefs') : undefined}
          />
        </div>
      ) : null}

      {zoeStreamingComplete && !pureProfit && result.intent !== 'capabilities_help' ? (
        <div className="zoe-snap-in">
          <MoreDetail
            brief={brief}
            showSummary={showSummary}
            figures={figures}
            open={moreDetailOpen}
            onOpenChange={setMoreDetailOpen}
            alsoInside={showEvidence ? [`timeline (${timelineCount})`, `evidence (${result.evidence?.length ?? 0})`] : []}
          >
            {timelineAndEvidence}
          </MoreDetail>
        </div>
      ) : null}

      {zoeStreamingComplete && result.conversation_id && result.investigation_id ? (
        <div className="zoe-snap-in" style={{ animationDelay: '60ms' }}>
          <AnswerFeedbackControl
            conversationId={result.conversation_id}
            runId={result.investigation_id}
            answerSeen={result.presentation?.narrative?.answer || result.summary}
          />
        </div>
      ) : null}

      {zoeStreamingComplete ? (
        <div className="zoe-snap-in" style={{ animationDelay: '120ms' }}>
          <FollowUps followUps={result.follow_ups} onSelect={onSelectFollowUp} isLoading={false} />
        </div>
      ) : null}
    </div>
  );
}
